import { sampleSizeBand, sampleSizeNote, type SampleSizeBand } from '../shared/sample-size.ts';
import { analyzeDroughts, chronologicalDroughts, type DroughtEpisode } from './drought.ts';
import { analyzeActivePace, type ActivePaceAnalysis } from './active-pace.ts';
import {
  DEFAULT_FIXED_STAKE,
  axisValues,
  assertMatrixRange,
  enumerateFixedMatrixCells,
  fixedHitNetPnl,
  fixedStakeProgression,
  fixedStopNetPnl,
  fixedWagerCount,
  theoreticalFixedFullStopExposure,
  type EntryStopRange,
} from './fixed-grid.ts';
import {
  REFERENCE_PACE_LABEL,
  REFERENCE_ROUNDS_PER_HOUR,
  breakEvenStopOutRate,
  extraFullStopsSurvivable,
  martingaleStakeProgression,
  martingaleWagerCount,
  pnlAfterOneExtraFullStop,
  profitExposureRatio,
  terminalEvidenceBand,
  theoreticalFullStopExposure,
  theoreticalFullStopLoss,
  type TerminalEvidenceBand,
} from './martingale-grid.ts';
import { martingaleSpec, simulatePortfolio } from './portfolio-simulator.ts';
import type { StrategyEpisode } from './simulator.ts';
import { maximumDrawdown } from './stats.ts';
import type { IndexedRound } from './types.ts';

export const ENTRY_STOP_MATRIX_PAGE_KIND = 'PROFIT_ENTRY_STOP_MATRIX' as const;
export const ENTRY_STOP_MATRIX_NOTE =
  'IN-SAMPLE HISTORICAL SIMULATION of every tested FIXED-stake Entry D / Stop S pair for the selected target. HIT = multiplier ≥ target. Historical net P&L is not a forecast, not future EV, and not a recommendation.';
export const ENTRY_STOP_MARTINGALE_MATRIX_NOTE =
  'IN-SAMPLE HISTORICAL SIMULATION of every tested 2x Martingale D/S pair. Historical net P&L is not a forecast, not future EV, and not a recommendation. No cell is labeled best, optimal, safest, or winner.';

export type MatrixStakingMode = 'FIXED' | 'MARTINGALE';

export interface EntryStopMatrixCell {
  entryDrought: number;
  stopDrought: number;
  wagerCount: number;
  stakeProgression: number[];
  theoreticalFullStopExposure: number;
  triggeredEpisodes: number;
  completedEpisodes: number;
  successfulEpisodes: number;
  stopOuts: number;
  openEpisodes: number;
  censoredEpisodes: number;
  successRate: number | null;
  stopOutRate: number | null;
  totalWagers: number;
  totalStaked: number;
  grossReturned: number;
  netPnl: number;
  roi: number | null;
  maximumDrawdown: number;
  largestActualWager: number;
  largestActualEpisodeExposure: number;
  observedLongestDrought: number;
  longestTriggeredDrought: number;
  stopExceedsObservedLongest: boolean;
  terminalStopOutObserved: boolean;
  terminalLossNotObservedNote: string | null;
  terminalEvidenceBand: TerminalEvidenceBand;
  sampleSizeBand: SampleSizeBand;
  sampleSizeNote: string;
  unitsPer1000Rounds: number | null;
  unitsPerActiveHour: number | null;
  unitsNormalizedAt180PerHour: number | null;
  entriesPerActiveHour: number | null;
  stopOutsPerActiveHour: number | null;
  entriesNormalizedAt180PerHour: number | null;
  stopOutsNormalizedAt180PerHour: number | null;
  profitExposureRatio: number | null;
  breakEvenStopOutRate: number | null;
  extraFullStopsSurvivable: number;
  pnlAfterOneExtraFullStop: number;
}

export interface MatrixEvidenceLists {
  zeroTerminalStopOuts: EntryStopMatrixCell[];
  lowTerminalEvidence: EntryStopMatrixCell[];
  moderateTerminalEvidence: EntryStopMatrixCell[];
  strongerTerminalEvidence: EntryStopMatrixCell[];
}

export interface EntryStopMatrixView {
  pageKind: typeof ENTRY_STOP_MATRIX_PAGE_KIND;
  claimKind: 'simulated_historical_profitability';
  sampleLabel: 'IN-SAMPLE HISTORICAL SIMULATION';
  note: string;
  hitRule: string;
  settlementRule: string;
  reentryRule: string;
  targetMultiplier: number;
  stakingMode: MatrixStakingMode;
  baseStake: number;
  entryFrom: number;
  entryTo: number;
  stopFrom: number;
  stopTo: number;
  requestedCells: number;
  validCells: number;
  referencePaceLabel: typeof REFERENCE_PACE_LABEL;
  referenceRoundsPerHour: typeof REFERENCE_ROUNDS_PER_HOUR;
  rounds: number;
  continuitySegments: number;
  observedLongestCompletedDrought: number;
  observedLongestDrought: number;
  pace: ActivePaceAnalysis;
  entryRows: number[];
  stopColumns: number[];
  cells: EntryStopMatrixCell[];
  invalidCellRule: string;
  evidence: MatrixEvidenceLists;
}

interface CellAccumulator {
  triggeredEpisodes: number;
  completedEpisodes: number;
  successfulEpisodes: number;
  stopOuts: number;
  openEpisodes: number;
  censoredEpisodes: number;
  totalWagers: number;
  totalStaked: number;
  grossReturned: number;
  netPnl: number;
  longestTriggeredDrought: number;
  largestActualWager: number;
  largestActualEpisodeExposure: number;
  completedPnl: number[];
}

export interface DroughtContribution {
  kind: 'none' | 'success' | 'failure' | 'open' | 'censored_gap';
  wagers: number;
  staked: number;
  grossReturned: number;
  pnl: number;
}

function rate(numer: number, denom: number): number | null {
  return denom === 0 ? null : numer / denom;
}

function perActiveHour(value: number, activeHours: number): number | null {
  return activeHours > 0 ? value / activeHours : null;
}

function normalizedAt180(value: number, rounds: number): number | null {
  return rounds > 0 ? (value * REFERENCE_ROUNDS_PER_HOUR) / rounds : null;
}

export function findMatrixCell(
  cells: readonly EntryStopMatrixCell[],
  entryDrought: number,
  stopDrought: number,
): EntryStopMatrixCell | null {
  return cells.find((cell) => cell.entryDrought === entryDrought && cell.stopDrought === stopDrought) ?? null;
}

export function classifyMatrixEvidence(cells: readonly EntryStopMatrixCell[]): MatrixEvidenceLists {
  return {
    zeroTerminalStopOuts: cells.filter((cell) => cell.stopOuts <= 0),
    lowTerminalEvidence: cells.filter((cell) => cell.stopOuts >= 1 && cell.stopOuts <= 4),
    moderateTerminalEvidence: cells.filter((cell) => cell.stopOuts >= 5 && cell.stopOuts <= 9),
    strongerTerminalEvidence: cells.filter((cell) => cell.stopOuts >= 10),
  };
}

/**
 * Map one target drought onto a FIXED D/S cell.
 * Drought length is consecutive misses; a terminating HIT is not a miss.
 * Sit-out after stop-out lasts only until that drought's HIT, so droughts are independent.
 */
export function fixedDroughtContribution(
  drought: DroughtEpisode,
  entryDrought: number,
  stopDrought: number,
  targetMultiplier: number,
  stake: number,
): DroughtContribution {
  const D = entryDrought;
  const S = stopDrought;
  const L = drought.lengthRounds;
  const W = fixedWagerCount(D, S);
  if (L < D) {
    return { kind: 'none', wagers: 0, staked: 0, grossReturned: 0, pnl: 0 };
  }
  if (L >= S) {
    const staked = theoreticalFixedFullStopExposure(W, stake);
    return {
      kind: 'failure',
      wagers: W,
      staked,
      grossReturned: 0,
      pnl: fixedStopNetPnl(W, stake),
    };
  }
  if (drought.status === 'completed') {
    const priorLosses = L - D;
    const wagers = priorLosses + 1;
    return {
      kind: 'success',
      wagers,
      staked: wagers * stake,
      grossReturned: stake * targetMultiplier,
      pnl: fixedHitNetPnl(targetMultiplier, priorLosses, stake),
    };
  }
  const missWagers = L - D;
  return {
    kind: drought.status,
    wagers: missWagers,
    staked: missWagers * stake,
    grossReturned: 0,
    pnl: -missWagers * stake,
  };
}

function emptyAccumulator(): CellAccumulator {
  return {
    triggeredEpisodes: 0,
    completedEpisodes: 0,
    successfulEpisodes: 0,
    stopOuts: 0,
    openEpisodes: 0,
    censoredEpisodes: 0,
    totalWagers: 0,
    totalStaked: 0,
    grossReturned: 0,
    netPnl: 0,
    longestTriggeredDrought: 0,
    largestActualWager: 0,
    largestActualEpisodeExposure: 0,
    completedPnl: [0],
  };
}

function applyContribution(
  acc: CellAccumulator,
  drought: DroughtEpisode,
  contrib: DroughtContribution,
  stake: number,
): void {
  if (contrib.kind === 'none') {
    return;
  }
  acc.triggeredEpisodes += 1;
  acc.longestTriggeredDrought = Math.max(acc.longestTriggeredDrought, drought.lengthRounds);
  acc.largestActualEpisodeExposure = Math.max(acc.largestActualEpisodeExposure, contrib.staked);
  if (contrib.wagers > 0) {
    acc.largestActualWager = Math.max(acc.largestActualWager, stake);
  }
  if (contrib.kind === 'success' || contrib.kind === 'failure') {
    acc.completedEpisodes += 1;
    acc.totalWagers += contrib.wagers;
    acc.totalStaked += contrib.staked;
    acc.grossReturned += contrib.grossReturned;
    acc.netPnl += contrib.pnl;
    acc.completedPnl.push(acc.netPnl);
    if (contrib.kind === 'success') {
      acc.successfulEpisodes += 1;
    } else {
      acc.stopOuts += 1;
    }
    return;
  }
  if (contrib.kind === 'open') {
    acc.openEpisodes += 1;
    return;
  }
  acc.censoredEpisodes += 1;
}

function toCell(
  spec: { entryDrought: number; stopDrought: number; wagerCount: number },
  acc: CellAccumulator,
  options: {
    stake: number;
    observedLongestDrought: number;
    rounds: number;
    pace: ActivePaceAnalysis;
  },
): EntryStopMatrixCell {
  const { stake, observedLongestDrought, rounds, pace } = options;
  const completed = acc.completedEpisodes;
  const stopOuts = acc.stopOuts;
  const successRate = rate(acc.successfulEpisodes, completed);
  const stopOutRate = rate(stopOuts, completed);
  const terminalStopOutObserved = stopOuts > 0;
  const fullStopExposure = theoreticalFixedFullStopExposure(spec.wagerCount, stake);
  const activeHours = pace.combinedActiveHours;
  const roi = completed === 0 || acc.totalStaked === 0 ? null : acc.netPnl / acc.totalStaked;
  return {
    entryDrought: spec.entryDrought,
    stopDrought: spec.stopDrought,
    wagerCount: spec.wagerCount,
    stakeProgression: fixedStakeProgression(spec.wagerCount, stake),
    theoreticalFullStopExposure: fullStopExposure,
    triggeredEpisodes: acc.triggeredEpisodes,
    completedEpisodes: completed,
    successfulEpisodes: acc.successfulEpisodes,
    stopOuts,
    openEpisodes: acc.openEpisodes,
    censoredEpisodes: acc.censoredEpisodes,
    successRate,
    stopOutRate,
    totalWagers: acc.totalWagers,
    totalStaked: acc.totalStaked,
    grossReturned: acc.grossReturned,
    netPnl: acc.netPnl,
    roi,
    maximumDrawdown: maximumDrawdown(acc.completedPnl),
    largestActualWager: acc.largestActualWager,
    largestActualEpisodeExposure: acc.largestActualEpisodeExposure,
    observedLongestDrought,
    longestTriggeredDrought: acc.longestTriggeredDrought,
    stopExceedsObservedLongest: spec.stopDrought > observedLongestDrought,
    terminalStopOutObserved,
    terminalLossNotObservedNote: terminalStopOutObserved ? null : 'Terminal loss not observed in this sample',
    terminalEvidenceBand: terminalEvidenceBand(stopOuts),
    sampleSizeBand: sampleSizeBand(completed),
    sampleSizeNote: sampleSizeNote(completed),
    unitsPer1000Rounds: rounds === 0 ? null : (acc.netPnl / rounds) * 1000,
    unitsPerActiveHour: perActiveHour(acc.netPnl, activeHours),
    unitsNormalizedAt180PerHour: normalizedAt180(acc.netPnl, rounds),
    entriesPerActiveHour: perActiveHour(acc.triggeredEpisodes, activeHours),
    stopOutsPerActiveHour: perActiveHour(stopOuts, activeHours),
    entriesNormalizedAt180PerHour: normalizedAt180(acc.triggeredEpisodes, rounds),
    stopOutsNormalizedAt180PerHour: normalizedAt180(stopOuts, rounds),
    profitExposureRatio: profitExposureRatio(acc.netPnl, fullStopExposure),
    breakEvenStopOutRate: null,
    extraFullStopsSurvivable: extraFullStopsSurvivable(acc.netPnl, fullStopExposure),
    pnlAfterOneExtraFullStop: pnlAfterOneExtraFullStop(acc.netPnl, fullStopExposure),
  };
}

function largestEpisodeExposure(episodes: readonly StrategyEpisode[]): number {
  let peak = 0;
  for (const episode of episodes) {
    peak = Math.max(peak, episode.totalStaked);
  }
  return peak;
}

export function completedEpisodeAccountingPnl(successes: number, stopOuts: number, wagerCount: number): number {
  return successes + stopOuts * theoreticalFullStopLoss(wagerCount);
}

/** Exact pre-713ae304 2x Martingale cell: martingaleSpec + simulatePortfolio. */
function simulateMartingaleCell(
  rounds: readonly IndexedRound[],
  entryDrought: number,
  stopDrought: number,
  observedLongestDrought: number,
  pace: ActivePaceAnalysis,
  baseStake: number,
): EntryStopMatrixCell {
  const spec = martingaleSpec(`d${entryDrought}-s${stopDrought}`, entryDrought, stopDrought, baseStake);
  const result = simulatePortfolio(rounds, [spec]);
  if (result.status !== 'ok') {
    throw new Error(result.note);
  }
  const metrics = result.strategies[0];
  if (!metrics) {
    throw new Error('Martingale matrix simulation produced no strategy metrics.');
  }
  const wagerCount = martingaleWagerCount(entryDrought, stopDrought);
  const completed = metrics.completedEpisodes;
  const stopOuts = metrics.stopOuts;
  const successRate = rate(metrics.successfulEpisodes, completed);
  const stopOutRate = rate(stopOuts, completed);
  const breakEven = breakEvenStopOutRate(wagerCount);
  const terminalStopOutObserved = stopOuts > 0;
  const triggered = metrics.episodes.length;
  const activeHours = pace.combinedActiveHours;
  const fullStopExposure = theoreticalFullStopExposure(wagerCount, baseStake);
  const longestTriggeredDrought = metrics.episodes.reduce(
    (max, episode) => Math.max(max, episode.endingDroughtLength),
    0,
  );
  return {
    entryDrought,
    stopDrought,
    wagerCount,
    stakeProgression: martingaleStakeProgression(wagerCount, baseStake),
    theoreticalFullStopExposure: fullStopExposure,
    triggeredEpisodes: triggered,
    completedEpisodes: completed,
    successfulEpisodes: metrics.successfulEpisodes,
    stopOuts,
    openEpisodes: metrics.openEpisodes,
    censoredEpisodes: metrics.censoredEpisodes,
    successRate,
    stopOutRate,
    totalWagers: metrics.totalWagers,
    totalStaked: metrics.totalStaked,
    grossReturned: metrics.grossReturned,
    netPnl: metrics.netPnl,
    roi: completed === 0 || metrics.totalStaked === 0 ? null : metrics.roi,
    maximumDrawdown: result.maximumDrawdown,
    largestActualWager: result.largestIndividualWager,
    largestActualEpisodeExposure: largestEpisodeExposure(metrics.episodes),
    observedLongestDrought,
    longestTriggeredDrought,
    stopExceedsObservedLongest: stopDrought > observedLongestDrought,
    terminalStopOutObserved,
    terminalLossNotObservedNote: terminalStopOutObserved ? null : 'Terminal loss not observed in this sample',
    terminalEvidenceBand: terminalEvidenceBand(stopOuts),
    sampleSizeBand: sampleSizeBand(completed),
    sampleSizeNote: sampleSizeNote(completed),
    unitsPer1000Rounds: rounds.length === 0 ? null : (metrics.netPnl / rounds.length) * 1000,
    unitsPerActiveHour: perActiveHour(metrics.netPnl, activeHours),
    unitsNormalizedAt180PerHour: normalizedAt180(metrics.netPnl, rounds.length),
    entriesPerActiveHour: perActiveHour(triggered, activeHours),
    stopOutsPerActiveHour: perActiveHour(stopOuts, activeHours),
    entriesNormalizedAt180PerHour: normalizedAt180(triggered, rounds.length),
    stopOutsNormalizedAt180PerHour: normalizedAt180(stopOuts, rounds.length),
    profitExposureRatio: profitExposureRatio(metrics.netPnl, fullStopExposure),
    breakEvenStopOutRate: breakEven,
    extraFullStopsSurvivable: extraFullStopsSurvivable(metrics.netPnl, fullStopExposure),
    pnlAfterOneExtraFullStop: pnlAfterOneExtraFullStop(metrics.netPnl, fullStopExposure),
  };
}

function buildMartingaleMatrixView(
  rounds: readonly IndexedRound[],
  range: EntryStopRange,
  baseStake: number,
): EntryStopMatrixView {
  const pace = analyzeActivePace(rounds);
  const droughtAnalysis = analyzeDroughts(rounds, 2);
  const observedLongestCompletedDrought = droughtAnalysis.longestCompletedDroughtRounds;
  const grid = enumerateFixedMatrixCells(range);
  const cells = grid.map((spec) =>
    simulateMartingaleCell(rounds, spec.entryDrought, spec.stopDrought, observedLongestCompletedDrought, pace, baseStake),
  );
  const requestedCells = (range.entryTo - range.entryFrom + 1) * (range.stopTo - range.stopFrom + 1);
  return {
    pageKind: ENTRY_STOP_MATRIX_PAGE_KIND,
    claimKind: 'simulated_historical_profitability',
    sampleLabel: 'IN-SAMPLE HISTORICAL SIMULATION',
    note: ENTRY_STOP_MARTINGALE_MATRIX_NOTE,
    hitRule: 'HIT = recorded multiplier ≥ 2x. MISS = recorded multiplier < 2x. Qualification uses the crash multiplier; settlement uses cash-out 2x.',
    settlementRule:
      'Successful completed episode nets +1 unit. Full stop loses 2^W − 1. Stakes are 1, 2, 4, …, 2^(W-1). Win gross uses cash-out 2x, never the crash multiplier.',
    reentryRule:
      'NO_REENTRY_UNTIL_HIT: after stop-out, sit out until a HIT. Continuity gaps censor an open betting episode and do not allow a drought or strategy to cross a proven gap.',
    targetMultiplier: 2,
    stakingMode: 'MARTINGALE',
    baseStake,
    entryFrom: range.entryFrom,
    entryTo: range.entryTo,
    stopFrom: range.stopFrom,
    stopTo: range.stopTo,
    requestedCells,
    validCells: cells.length,
    referencePaceLabel: REFERENCE_PACE_LABEL,
    referenceRoundsPerHour: REFERENCE_ROUNDS_PER_HOUR,
    rounds: rounds.length,
    continuitySegments: new Set(rounds.map((round) => round.segmentId)).size,
    observedLongestCompletedDrought,
    observedLongestDrought: droughtAnalysis.longestDroughtRounds,
    pace,
    entryRows: axisValues(range.entryFrom, range.entryTo),
    stopColumns: axisValues(range.stopFrom, range.stopTo),
    cells,
    invalidCellRule:
      'Blank cells are invalid: Stop S must be an integer ≥ Entry D + 1 so at least one wager can be placed.',
    evidence: classifyMatrixEvidence(cells),
  };
}

export function buildEntryStopMatrixView(
  rounds: readonly IndexedRound[],
  options: {
    targetMultiplier?: number;
    stakingMode?: MatrixStakingMode;
    baseStake?: number;
    entryFrom?: number;
    entryTo?: number;
    stopFrom?: number;
    stopTo?: number;
  } = {},
): EntryStopMatrixView {
  const targetMultiplier = options.targetMultiplier ?? 2;
  const stakingMode = options.stakingMode ?? 'FIXED';
  const baseStake = options.baseStake ?? DEFAULT_FIXED_STAKE;
  if (!Number.isFinite(baseStake) || baseStake <= 0) {
    throw new Error('Fixed stake must be a finite number > 0.');
  }
  if (!Number.isFinite(targetMultiplier) || targetMultiplier < 1) {
    throw new Error('Target multiplier must be a finite number >= 1.');
  }
  const range: EntryStopRange = {
    entryFrom: options.entryFrom ?? 2,
    entryTo: options.entryTo ?? 10,
    stopFrom: options.stopFrom ?? 4,
    stopTo: options.stopTo ?? 21,
  };
  assertMatrixRange(range);
  if (stakingMode === 'MARTINGALE') {
    if (targetMultiplier !== 2) {
      throw new Error('MARTINGALE is available only for target 2x.');
    }
    return buildMartingaleMatrixView(rounds, range, baseStake);
  }
  if (stakingMode !== 'FIXED') {
    throw new Error(
      'The entry/stop matrix supports FIXED staking, or MARTINGALE for target 2x. Use the single-strategy calculator for Recovery.',
    );
  }
  const pace = analyzeActivePace(rounds);
  const droughtAnalysis = analyzeDroughts(rounds, targetMultiplier);
  const droughts = chronologicalDroughts(droughtAnalysis);
  const observedLongestCompletedDrought = droughtAnalysis.longestCompletedDroughtRounds;
  const grid = enumerateFixedMatrixCells(range);
  const accumulators = new Map<string, CellAccumulator>();
  for (const spec of grid) {
    accumulators.set(`${spec.entryDrought}:${spec.stopDrought}`, emptyAccumulator());
  }

  for (const drought of droughts) {
    for (const spec of grid) {
      const acc = accumulators.get(`${spec.entryDrought}:${spec.stopDrought}`);
      if (!acc) {
        continue;
      }
      const contrib = fixedDroughtContribution(
        drought,
        spec.entryDrought,
        spec.stopDrought,
        targetMultiplier,
        baseStake,
      );
      applyContribution(acc, drought, contrib, baseStake);
    }
  }

  const cells = grid.map((spec) => {
    const acc = accumulators.get(`${spec.entryDrought}:${spec.stopDrought}`);
    if (!acc) {
      throw new Error(`Missing matrix accumulator for D${spec.entryDrought}/S${spec.stopDrought}.`);
    }
    return toCell(spec, acc, {
      stake: baseStake,
      observedLongestDrought: observedLongestCompletedDrought,
      rounds: rounds.length,
      pace,
    });
  });

  const requestedCells = (range.entryTo - range.entryFrom + 1) * (range.stopTo - range.stopFrom + 1);
  return {
    pageKind: ENTRY_STOP_MATRIX_PAGE_KIND,
    claimKind: 'simulated_historical_profitability',
    sampleLabel: 'IN-SAMPLE HISTORICAL SIMULATION',
    note: ENTRY_STOP_MATRIX_NOTE,
    hitRule: `HIT = recorded multiplier ≥ ${targetMultiplier}x. MISS = recorded multiplier < ${targetMultiplier}x. Qualification uses the crash multiplier; settlement uses the selected target.`,
    settlementRule:
      'Win net = stake*(M-1), gross = stake*M, using cash-out target M, never the crash multiplier. Lose = -stake. Every wager in an episode uses the same fixed stake.',
    reentryRule:
      'NO_REENTRY_UNTIL_HIT: after stop-out, sit out until a HIT. Continuity gaps censor an open betting episode and do not allow a drought or strategy to cross a proven gap.',
    targetMultiplier,
    stakingMode,
    baseStake,
    entryFrom: range.entryFrom,
    entryTo: range.entryTo,
    stopFrom: range.stopFrom,
    stopTo: range.stopTo,
    requestedCells,
    validCells: cells.length,
    referencePaceLabel: REFERENCE_PACE_LABEL,
    referenceRoundsPerHour: REFERENCE_ROUNDS_PER_HOUR,
    rounds: rounds.length,
    continuitySegments: new Set(rounds.map((round) => round.segmentId)).size,
    observedLongestCompletedDrought,
    observedLongestDrought: droughtAnalysis.longestDroughtRounds,
    pace,
    entryRows: axisValues(range.entryFrom, range.entryTo),
    stopColumns: axisValues(range.stopFrom, range.stopTo),
    cells,
    invalidCellRule:
      'Blank cells are invalid: Stop S must be an integer ≥ Entry D + 1 so at least one wager can be placed.',
    evidence: classifyMatrixEvidence(cells),
  };
}
